import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';

import StandardDialog, { StandardDialogActions } from '../Modals/StandardDialog';
import { getCompanyReferences, searchCompanyReferences } from '../../services/companyDetail';
import { USER_TYPES } from '../../services/user';
import { useAppSelector } from '../../app/hooks';
import {
  assignFeatureFlagUsers,
  createFeatureFlag,
  deleteFeatureFlag,
  getFeatureFlags,
  getFeatureFlagUsers,
  patchFeatureFlag,
  removeFeatureFlagUsers,
  searchUsersByEmail,
} from '../../services/featureFlags';
import { FeatureFlag, FeatureFlagUser } from '../../interfaces/featureFlag.interface';

const CAN_ADMIN_FEATURE_FLAGS = 'can_admin_feature_flags';
const NO_PERMISSION_MESSAGE =
  'You do not have permission to manage feature flags. Ask a TPN admin with the '
  + `${CAN_ADMIN_FEATURE_FLAGS} permission to make this change.`;

const USER_TYPE_OPTIONS = Object.values(USER_TYPES);

const USER_TYPE_LABELS: Record<string, string> = {
  tpn_admin: 'TPN Admins',
  vendor_admin: 'Service Providers',
  content_owner: 'Content Owners',
  assessor: 'Assessors',
};

const userTypeLabel = (value: string): string => USER_TYPE_LABELS[value] ?? value;

interface CompanyOption {
  id: number;
  name: string;
}

interface FlagFormValues {
  description: string;
  isActive: boolean;
  isPublic: boolean;
  startsAt: string;
  endsAt: string;
  targetUserTypes: string[];
  targetCompanies: CompanyOption[];
}

const EMPTY_FORM = {
  name: '',
  codename: '',
  description: '',
  isActive: true,
  isPublic: false,
  startsAt: '',
  endsAt: '',
  targetUserTypes: [] as string[],
  targetCompanies: [] as CompanyOption[],
};

/** The admin-facing message for a failed request: the API's own complaint when it sends one. */
function requestErrorMessage(error: any, fallback: string): string {
  const data = error?.response?.data;
  return data?.codename?.[0] ?? data?.detail ?? fallback;
}

function toIsoOrNull(value: string): string | null {
  return value ? new Date(value).toISOString() : null;
}

function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface FlagFieldsProps {
  values: FlagFormValues;
  onChange: (patch: Partial<FlagFormValues>) => void;
  companyOptions: CompanyOption[];
  onCompanySearch: (query: string) => void;
}

/** Failure feedback, rendered wherever the admin currently is (panel or open dialog). */
function ErrorAlert({ message, onDismiss }: { message: string | null; onDismiss: () => void }) {
  if (!message) return null;
  return <Alert severity="error" sx={{ mb: 2 }} onClose={onDismiss}>{message}</Alert>;
}

/** The editable fields shared by the create and edit dialogs. */
function FlagFields({ values, onChange, companyOptions, onCompanySearch }: FlagFieldsProps) {
  return (
    <>
      <TextField
        label="Description"
        multiline
        minRows={2}
        value={values.description}
        onChange={(e) => onChange({ description: e.target.value })}
      />
      <Stack direction="row" gap={4}>
        <Stack>
          <FormControlLabel
            control={<Switch checked={values.isActive} onChange={(e) => onChange({ isActive: e.target.checked })} />}
            label="Active"
          />
          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 260 }}>
            Master switch. When off, the feature is off for <strong>everyone</strong> — even individually assigned users.
          </Typography>
        </Stack>
        <Stack>
          <FormControlLabel
            control={<Switch checked={values.isPublic} onChange={(e) => onChange({ isPublic: e.target.checked })} />}
            label="Public (everyone)"
          />
          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 260 }}>
            When on, every user gets the feature — no targeting or assignment needed.
          </Typography>
        </Stack>
      </Stack>
      <Stack direction="row" gap={2}>
        <TextField
          label="Starts at"
          type="datetime-local"
          InputLabelProps={{ shrink: true }}
          helperText="Optional. Off before this time."
          value={values.startsAt}
          onChange={(e) => onChange({ startsAt: e.target.value })}
        />
        <TextField
          label="Ends at"
          type="datetime-local"
          InputLabelProps={{ shrink: true }}
          helperText="Optional. Off after this time."
          value={values.endsAt}
          onChange={(e) => onChange({ endsAt: e.target.value })}
        />
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Leave a bound blank for open-ended. Outside the schedule the feature is off for everyone, even assigned users.
      </Typography>
      <Autocomplete
        multiple
        options={USER_TYPE_OPTIONS}
        value={values.targetUserTypes}
        getOptionLabel={(option) => userTypeLabel(option)}
        onChange={(_e, value) => onChange({ targetUserTypes: value })}
        renderInput={(params) => <TextField {...params} label="Target user types" />}
      />
      <Autocomplete
        multiple
        options={companyOptions}
        value={values.targetCompanies}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        filterOptions={(x) => x}
        onInputChange={(_e, value) => onCompanySearch(value)}
        onChange={(_e, value) => onChange({ targetCompanies: value })}
        renderInput={(params) => <TextField {...params} label="Target companies" placeholder="Search companies" />}
      />
    </>
  );
}

export default function FeatureFlagsAdminPanel() {
  const { user } = useAppSelector((state) => state.user);
  const canAdminFeatureFlags = React.useMemo(
    () => user?.permissions?.includes(CAN_ADMIN_FEATURE_FLAGS),
    [user],
  );

  const [rows, setRows] = useState<FeatureFlag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [companyOptions, setCompanyOptions] = useState<CompanyOption[]>([]);
  const [saving, setSaving] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [actionError, setActionError] = useState<string | null>(null);

  const [editFlag, setEditFlag] = useState<FeatureFlag | null>(null);
  const [editForm, setEditForm] = useState<FlagFormValues | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<FeatureFlag | null>(null);

  const [usersFlag, setUsersFlag] = useState<FeatureFlag | null>(null);
  const [assignedUsers, setAssignedUsers] = useState<FeatureFlagUser[]>([]);
  const [userOptions, setUserOptions] = useState<FeatureFlagUser[]>([]);

  /** Report a failed request to the admin instead of leaving the UI silently unchanged. */
  const reportError = (fallback: string) => (error: any) =>
    setActionError(requestErrorMessage(error, fallback));

  useEffect(() => {
    getFeatureFlags({ limit: 200 })
      .then((response) => setRows(response.results))
      .catch(reportError('Could not load feature flags.'))
      .finally(() => setIsLoading(false));
  }, []);

  const handleCompanySearch = (query: string) => {
    searchCompanyReferences(query)
      .then((refs) => setCompanyOptions(refs.map((ref) => ({ id: ref.id, name: ref.name ?? '' }))))
      .catch(reportError('Could not search companies.'));
  };

  const handleUserSearch = (query: string) => {
    searchUsersByEmail(query)
      .then(setUserOptions)
      .catch(reportError('Could not search users.'));
  };

  const updateRow = (flag: FeatureFlag) =>
    setRows((current) => current.map((row) => (row.id === flag.id ? flag : row)));

  const handleToggle = (flag: FeatureFlag, field: 'isActive' | 'isPublic') => {
    if (!canAdminFeatureFlags) return;
    setActionError(null);
    patchFeatureFlag(flag.id, { [field]: !flag[field] })
      .then(updateRow)
      .catch(reportError(`Could not update ${flag.codename}.`));
  };

  const handleCreate = async () => {
    if (!canAdminFeatureFlags) return;
    setSaving(true);
    setActionError(null);
    try {
      const created = await createFeatureFlag({
        name: form.name,
        codename: form.codename,
        description: form.description,
        isActive: form.isActive,
        isPublic: form.isPublic,
        startsAt: toIsoOrNull(form.startsAt),
        endsAt: toIsoOrNull(form.endsAt),
        targetUserTypes: form.targetUserTypes,
        targetCompanies: form.targetCompanies.map((c) => c.id),
      });
      setRows((current) => [created, ...current]);
      setForm({ ...EMPTY_FORM });
      setCreateOpen(false);
    } catch (error: any) {
      setActionError(requestErrorMessage(error, 'Could not create feature flag.'));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (flag: FeatureFlag) => {
    if (!canAdminFeatureFlags) return;
    setActionError(null);
    setEditFlag(flag);
    setCompanyOptions([]);
    setEditForm({
      description: flag.description ?? '',
      isActive: flag.isActive,
      isPublic: flag.isPublic,
      startsAt: isoToLocalInput(flag.startsAt),
      endsAt: isoToLocalInput(flag.endsAt),
      targetUserTypes: flag.targetUserTypes ?? [],
      targetCompanies: [],
    });
    const ids = flag.targetCompanies ?? [];
    if (ids.length) {
      getCompanyReferences(ids)
        .then((refs) => {
          const options = refs.map((r) => ({ id: r.id as number, name: r.name ?? '' }));
          setEditForm((current) => (current ? { ...current, targetCompanies: options } : current));
        })
        .catch(reportError('Could not load the targeted companies for this flag.'));
    }
  };

  const handleEditSave = async () => {
    if (!editFlag || !editForm) return;
    setSaving(true);
    setActionError(null);
    try {
      const updated = await patchFeatureFlag(editFlag.id, {
        description: editForm.description,
        isActive: editForm.isActive,
        isPublic: editForm.isPublic,
        startsAt: toIsoOrNull(editForm.startsAt),
        endsAt: toIsoOrNull(editForm.endsAt),
        targetUserTypes: editForm.targetUserTypes,
        targetCompanies: editForm.targetCompanies.map((c) => c.id),
      });
      updateRow(updated);
      setEditFlag(null);
      setEditForm(null);
    } catch (error: any) {
      setActionError(requestErrorMessage(error, `Could not save ${editFlag.codename}.`));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!canAdminFeatureFlags || !deleteTarget) return;
    const { id, codename } = deleteTarget;
    setActionError(null);
    deleteFeatureFlag(id)
      .then(() => {
        setRows((current) => current.filter((row) => row.id !== id));
        setDeleteTarget(null);
      })
      .catch(reportError(`Could not delete ${codename}.`));
  };

  const openUsers = (flag: FeatureFlag) => {
    if (!canAdminFeatureFlags) return;
    setActionError(null);
    setUsersFlag(flag);
    setUserOptions([]);
    getFeatureFlagUsers(flag.id, { limit: 200 })
      .then((response) => setAssignedUsers(response.results))
      .catch(reportError(`Could not load the users assigned to ${flag.codename}.`));
  };

  const handleAssign = (assignee: FeatureFlagUser | null) => {
    if (!canAdminFeatureFlags || !usersFlag || !assignee) return;
    setActionError(null);
    assignFeatureFlagUsers(usersFlag.id, [assignee.id])
      .then((flag) => {
        updateRow(flag);
        setUsersFlag(flag);
        setAssignedUsers((current) => (current.some((u) => u.id === assignee.id) ? current : [...current, assignee]));
      })
      .catch(reportError(`Could not assign ${assignee.email}.`));
  };

  const handleRemove = (assignee: FeatureFlagUser) => {
    if (!canAdminFeatureFlags || !usersFlag) return;
    setActionError(null);
    removeFeatureFlagUsers(usersFlag.id, [assignee.id])
      .then((flag) => {
        updateRow(flag);
        setUsersFlag(flag);
        setAssignedUsers((current) => current.filter((u) => u.id !== assignee.id));
      })
      .catch(reportError(`Could not remove ${assignee.email}.`));
  };

  // A dialog renders its own copy of the error, so the panel keeps quiet while one is open.
  const dialogOpen = createOpen || !!editFlag || !!usersFlag || !!deleteTarget;

  const stop = (event: React.MouseEvent) => event.stopPropagation();

  const columns: GridColDef[] = [
    // Name/Codename/Targeting flex into the leftover width so long names aren't
    // truncated on wide screens; the switch and action columns stay fixed.
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Tooltip title={params.row.name}>
          <Typography variant="body2" noWrap>{params.row.name}</Typography>
        </Tooltip>
      ),
    },
    { field: 'codename', headerName: 'Codename', flex: 1.2, minWidth: 230 },
    {
      field: 'isActive',
      headerName: 'Active',
      width: 90,
      renderCell: (params) => (
        <Tooltip title={canAdminFeatureFlags ? '' : NO_PERMISSION_MESSAGE}>
          <span onClick={stop}>
            <Switch
              checked={params.row.isActive}
              disabled={!canAdminFeatureFlags}
              onChange={() => handleToggle(params.row, 'isActive')}
              inputProps={{ 'aria-label': `toggle active ${params.row.codename}` }}
            />
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'isPublic',
      headerName: 'Public',
      width: 90,
      renderCell: (params) => (
        <Tooltip title={canAdminFeatureFlags ? '' : NO_PERMISSION_MESSAGE}>
          <span onClick={stop}>
            <Switch
              checked={params.row.isPublic}
              disabled={!canAdminFeatureFlags}
              onChange={() => handleToggle(params.row, 'isPublic')}
              inputProps={{ 'aria-label': `toggle public ${params.row.codename}` }}
            />
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'targets',
      headerName: 'Targeting',
      flex: 1,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => {
        const types = (params.row.targetUserTypes ?? []) as string[];
        const companyCount = (params.row.targetCompanies ?? []).length;
        const bits: string[] = [];
        if (types.length) bits.push(`types: ${types.map(userTypeLabel).join(', ')}`);
        if (companyCount) bits.push(`${companyCount} compan${companyCount === 1 ? 'y' : 'ies'}`);
        const summary = bits.join(' · ') || '—';
        return (
          <Tooltip title={summary}>
            <Typography variant="body2" noWrap>{summary}</Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'assignedUserCount',
      headerName: 'Users',
      width: 120,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" gap={0.5} onClick={stop}>
          <span>{params.row.assignedUserCount}</span>
          <Tooltip title={canAdminFeatureFlags ? '' : NO_PERMISSION_MESSAGE}>
            <span>
              <IconButton
                size="small"
                disabled={!canAdminFeatureFlags}
                aria-label={`manage users ${params.row.codename}`}
                onClick={() => openUsers(params.row)}
              >
                <PeopleIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      ),
    },
    {
      field: 'action',
      headerName: 'Delete',
      width: 90,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title={canAdminFeatureFlags ? '' : NO_PERMISSION_MESSAGE}>
          <span>
            <IconButton
              size="small"
              disabled={!canAdminFeatureFlags}
              aria-label={`delete ${params.row.codename}`}
              onClick={(e) => { stop(e); setDeleteTarget(params.row); }}
            >
              <DeleteIcon />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <Card sx={{ padding: '2em' }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle>How a feature flag turns on for a user</AlertTitle>
        A user gets the feature only when it is <strong>Active</strong> <em>and</em> the current time is within its
        schedule window, <em>and</em> at least one of these is true:
        <ul style={{ margin: '0.5em 0 0', paddingLeft: '1.25em' }}>
          <li><strong>Public</strong> is on (everyone gets it), or</li>
          <li>the user&apos;s <strong>user type</strong> is targeted, or</li>
          <li>the user&apos;s <strong>company</strong> is targeted, or</li>
          <li>the user is <strong>individually assigned</strong> (the Users button).</li>
        </ul>
        Turning <strong>Active</strong> off — or being outside the schedule — disables the feature for everyone,
        including individually-assigned users. Click a row to edit all of these.
        <Typography variant="body2" sx={{ mt: 1 }}>
          <strong>Note:</strong> Public and targeting (type/company) only affect <em>client-side</em> features (e.g. the
          new company profile). Server-enforced permissions such as masquerade are honored only for users added
          individually via the <strong>Users</strong> button.
        </Typography>
      </Alert>
      {!canAdminFeatureFlags && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {NO_PERMISSION_MESSAGE}
        </Alert>
      )}
      <ErrorAlert message={dialogOpen ? null : actionError} onDismiss={() => setActionError(null)} />
      <Stack direction="row-reverse" spacing={1} mb={2}>
        <Tooltip title={canAdminFeatureFlags ? '' : NO_PERMISSION_MESSAGE}>
          <span>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              disabled={!canAdminFeatureFlags}
              onClick={() => { setActionError(null); setCreateOpen(true); }}
            >
              Add Feature Flag
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <DataGridPro
        style={{ height: 460, width: '100%' }}
        rows={rows}
        columns={columns}
        pageSize={25}
        rowsPerPageOptions={[25]}
        loading={isLoading}
        disableSelectionOnClick
        onRowClick={(params) => openEdit(params.row as FeatureFlag)}
        sx={{ '& .MuiDataGrid-row': { cursor: canAdminFeatureFlags ? 'pointer' : 'default' } }}
      />

      {/* Create */}
      <StandardDialog title="Add Feature Flag" handleClose={() => { setActionError(null); setCreateOpen(false); }} isOpen={createOpen}>
        <Stack gap={2} sx={{ mt: 1 }}>
          <TextField
            required
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            required
            label="Codename"
            helperText="The permission codename the client checks (e.g. can_view_v2_company_profile)."
            value={form.codename}
            onChange={(e) => setForm({ ...form, codename: e.target.value })}
          />
          <FlagFields
            values={form}
            onChange={(patch) => setForm({ ...form, ...patch })}
            companyOptions={companyOptions}
            onCompanySearch={handleCompanySearch}
          />
          <ErrorAlert message={actionError} onDismiss={() => setActionError(null)} />
        </Stack>
        <StandardDialogActions>
          <Button variant="outlined" onClick={() => { setActionError(null); setCreateOpen(false); }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={saving || !form.name || !form.codename}
            onClick={handleCreate}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}
          >
            Add
          </Button>
        </StandardDialogActions>
      </StandardDialog>

      {/* Edit */}
      <StandardDialog
        title={editFlag ? `Edit — ${editFlag.name}` : 'Edit'}
        handleClose={() => { setActionError(null); setEditFlag(null); setEditForm(null); }}
        isOpen={!!editFlag && !!editForm}
      >
        {editFlag && editForm && (
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextField label="Name" value={editFlag.name} disabled />
            <TextField
              label="Codename"
              value={editFlag.codename}
              disabled
              helperText="Codename is fixed once created (the client checks against it)."
            />
            <FlagFields
              values={editForm}
              onChange={(patch) => setEditForm({ ...editForm, ...patch })}
              companyOptions={companyOptions}
              onCompanySearch={handleCompanySearch}
            />
            <ErrorAlert message={actionError} onDismiss={() => setActionError(null)} />
          </Stack>
        )}
        <StandardDialogActions>
          <Button variant="outlined" onClick={() => { setActionError(null); setEditFlag(null); setEditForm(null); }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={handleEditSave}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}
          >
            Save
          </Button>
        </StandardDialogActions>
      </StandardDialog>

      {/* Manage users */}
      <StandardDialog
        title={usersFlag ? `Users — ${usersFlag.name}` : 'Users'}
        handleClose={() => { setActionError(null); setUsersFlag(null); }}
        isOpen={!!usersFlag}
      >
        <Stack gap={2} sx={{ mt: 1 }}>
          <Autocomplete
            options={userOptions}
            getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterOptions={(x) => x}
            onInputChange={(_e, value) => handleUserSearch(value)}
            onChange={(_e, value) => handleAssign(value)}
            renderInput={(params) => <TextField {...params} label="Assign a user by email" />}
          />
          <ErrorAlert message={actionError} onDismiss={() => setActionError(null)} />
          <Stack direction="row" gap={1} flexWrap="wrap">
            {assignedUsers.length === 0 && <Typography variant="body2">No users assigned.</Typography>}
            {assignedUsers.map((assignee) => (
              <Chip
                key={assignee.id}
                label={assignee.email}
                onDelete={canAdminFeatureFlags ? () => handleRemove(assignee) : undefined}
              />
            ))}
          </Stack>
        </Stack>
        <StandardDialogActions>
          <Button variant="outlined" onClick={() => setUsersFlag(null)}>Done</Button>
        </StandardDialogActions>
      </StandardDialog>

      {/* Delete */}
      <StandardDialog title="Delete Feature Flag" handleClose={() => { setActionError(null); setDeleteTarget(null); }} isOpen={!!deleteTarget}>
        <Typography>Delete {deleteTarget?.name}?</Typography>
        <Typography color="error">
          This deletes the backing permission and revokes the feature from every user.
        </Typography>
        <ErrorAlert message={actionError} onDismiss={() => setActionError(null)} />
        <StandardDialogActions>
          <Button variant="outlined" onClick={() => { setActionError(null); setDeleteTarget(null); }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </StandardDialogActions>
      </StandardDialog>
    </Card>
  );
}
